import React from "react";
import { IconButton } from "@fluentui/react/lib/Button";
import { FilterDropDown, FilterDropDownItem } from "../../generic/filterBar/FilterDropDown";
import { formatMessageDefault } from "../../../utils/helpers/LanguageHelper";
import { OutlineFilterStatus } from "../workflowStatusCategory";
import "./outline-search.css";

/** IDS-5755: the outline-search filter state (workflow status + "assigned to me"), owned by the host. */
export interface OutlineSearchBarFilter {
  assignedToMe: boolean;
  statusFilter: OutlineFilterStatus | null;
}

export interface OutlineSearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  onSubmit?: () => void;
  /** IDS-5755: shows the filter button + dropdown to the right of the input. Defaults to false (hidden). */
  enableFilter?: boolean;
  filter?: OutlineSearchBarFilter;
  onToggleAssignedToMe?: () => void;
  onSelectStatus?: (category: OutlineFilterStatus | null) => void;
}

const DEFAULT_FILTER: OutlineSearchBarFilter = { assignedToMe: false, statusFilter: null };

/**
 * IDS-5755: stable, top-level renderer for a status option's `assets/certification/*.svg` icon.
 * Kept outside `OutlineSearchBar` (SonarQube S6478 — components must not be defined during render).
 */
const StatusIcon: React.FC<{ asset: string }> = ({ asset }) => (
  <img
    src={`/assets/certification/${asset}.svg`}
    alt=""
    aria-hidden="true"
    className="outline-filter-status-icon"
    width={16}
    height={16}
  />
);

/** Builds a `FilterDropDownItem.icon` renderer for `asset`, defined outside the component (S6478). */
const createStatusIcon = (asset: string): (() => React.ReactElement) =>
  function renderStatusIcon() {
    return <StatusIcon asset={asset} />;
  };

/**
 * Status options in the exact dropdown order required by the plan, each paired with its
 * `assets/certification/*.svg` icon (FE-1 category table — reused verbatim, this is presentation-only
 * category->asset/caption mapping, NOT a new classification) and its i18n caption id + default fallback.
 */
const STATUS_OPTIONS: { category: OutlineFilterStatus; asset: string; captionId: string; defaultLabel: string }[] = [
  {
    category: OutlineFilterStatus.NOT_RELEVANT,
    asset: "notsignificant",
    captionId: "cof.outline.search.filter.status.notrelevant",
    defaultLabel: "Not Relevant",
  },
  {
    category: OutlineFilterStatus.NOT_STARTED,
    asset: "initialstate",
    captionId: "cof.outline.search.filter.status.notstarted",
    defaultLabel: "Not Started",
  },
  {
    category: OutlineFilterStatus.IN_PROGRESS,
    asset: "inprogress",
    captionId: "cof.outline.search.filter.status.inprogress",
    defaultLabel: "In Progress",
  },
  {
    category: OutlineFilterStatus.PENDING_APPROVAL,
    asset: "pending-blocked",
    captionId: "cof.outline.search.filter.status.pendingapproval",
    defaultLabel: "Pending Approval",
  },
  {
    category: OutlineFilterStatus.TO_BE_APPROVED,
    asset: "pending-available",
    captionId: "cof.outline.search.filter.status.tobeapproved",
    defaultLabel: "To Be Approved",
  },
  {
    category: OutlineFilterStatus.SUBMITTED,
    asset: "submission",
    captionId: "cof.outline.search.filter.status.submitted",
    defaultLabel: "Submitted",
  },
  {
    category: OutlineFilterStatus.REJECTED,
    asset: "rejected",
    captionId: "cof.outline.search.filter.status.rejected",
    defaultLabel: "Rejected",
  },
];

/** Sticky search bar for the outline panel — no autofocus; optional status/assigned-to-me filter dropdown */
export const OutlineSearchBar: React.FC<OutlineSearchBarProps> = ({
  value,
  onChange,
  onClear,
  onSubmit,
  enableFilter = false,
  filter,
  onToggleAssignedToMe,
  onSelectStatus,
}) => {
  const currentFilter = filter ?? DEFAULT_FILTER;

  // Option items (All/Clear + the 7 statuses) share FilterDropDown's existing setActiveFilters path.
  // `values` is `[]` for All/Clear (value:undefined) or `[category]` for a status pick.
  const handleSetActiveFilters = (values: unknown[]) => {
    const selected = values?.[0] as OutlineFilterStatus | undefined;
    if (selected === undefined) {
      onSelectStatus?.(null);
      return;
    }
    // Re-clicking the already-active status deselects it (maps to All/Clear).
    onSelectStatus?.(selected === currentFilter.statusFilter ? null : selected);
  };

  const filterItems: FilterDropDownItem[] = [
    {
      type: "checkbox",
      key: "assignedToMe",
      checked: currentFilter.assignedToMe,
      label: formatMessageDefault("cof.outline.search.filter.assignedtome", "Assigned to me"),
      testId: "outline-filter-assigned-to-me",
      value: "assignedToMe",
    },
    {
      type: "separator",
      key: "sep",
      testId: "outline-filter-separator",
      label: "",
      value: undefined,
    },
    {
      type: "option",
      key: "all",
      value: undefined,
      label: formatMessageDefault("cof.outline.search.filter.all", "All"),
      testId: "outline-filter-all",
      active: currentFilter.statusFilter === null,
    },
    ...STATUS_OPTIONS.map(
      ({ category, asset, captionId, defaultLabel }): FilterDropDownItem => ({
        type: "option",
        key: category,
        value: category,
        label: formatMessageDefault(captionId, defaultLabel),
        icon: createStatusIcon(asset),
        active: currentFilter.statusFilter === category,
        testId: `outline-filter-status-${category}`,
      })
    ),
  ];

  return (
    <div className="outline-search-bar-wrap">
      <div className="outline-search-bar-input-wrap">
        <img
          src="/assets/search.svg"
          alt=""
          aria-hidden="true"
          className="outline-search-bar-icon"
          width={14}
          height={14}
        />
        <input
          type="text"
          className="outline-search-bar-input"
          placeholder="Search..."
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && onSubmit) {
              e.preventDefault();
              onSubmit();
            }
          }}
          aria-label="Search..."
          autoFocus={false}
        />
        {value.length > 0 && (
          <IconButton
            iconProps={{ iconName: "ErrorBadge" }}
            title="Clear search"
            ariaLabel="Clear search"
            styles={{
              root: { height: 16, width: 16, minWidth: "unset", padding: 0, backgroundColor: "transparent" },
              rootHovered: { backgroundColor: "transparent" },
              rootPressed: { backgroundColor: "transparent" },
              icon: { color: "#a19f9d", fontSize: 16, lineHeight: 16, margin: 0 },
              iconHovered: { color: "#605e5c" },
              iconPressed: { color: "#323130" },
            }}
            onClick={onClear}
          />
        )}
      </div>
      {enableFilter && (
        <div className="outline-search-bar-filter-wrap" data-testid="outline-filter-button">
          <FilterDropDown
            filters={filterItems}
            setActiveFilters={handleSetActiveFilters}
            onToggleItem={() => onToggleAssignedToMe?.()}
          />
        </div>
      )}
    </div>
  );
};
